import React, { useRef } from 'react'
import Slider, { Settings } from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import styled, { css } from 'styled-components'
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from 'react-icons/io'
import { FlexItem, FlexLayout, GridLayout } from '@eduact/ed-system'
import { NavIcon } from '../Swiper/Swiper.styled'
type ItemProps = {
	label: string
}

const SwiperItemWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`
const SwiperItem: React.FC<ItemProps> = ({ children }) => <SwiperItemWrapper className="SliderItem">{children}</SwiperItemWrapper>

interface SwiperComposition {
	Item: React.FC<ItemProps>
}
type Props = {
	perSlide?: number
	/** Hide the prev/next controls — for lists too short to page through. */
	withArrows?: boolean
	children: React.ReactElement<ItemProps>[]
}
const DESKTOP_PER_VIEW = 7
const MOBILE_PER_VIEW = 3

const AutoPlaySwiper: React.FC<Props> & SwiperComposition = ({ children, perSlide = 4, withArrows = true }) => {
	const swiperRef = useRef<Slider | null>(null)
	const count = React.Children.count(children)
	// `infinite` defaults to true, and when it is on slick pads the track with
	// clones: getPreClones returns slidesToShow and getPostClones returns
	// slideCount. With one instructor against slidesToShow 3 that is three
	// pre-clones + the real slide + one post-clone, so the same person rendered
	// five times. Loop only when there is genuinely more than a screenful, per
	// breakpoint — the mobile view shows fewer, so it reaches that point sooner.
	const settings: Settings = {
		cssEase: 'linear',
		// autoplay: true,
		autoplaySpeed: 4000,
		slidesToShow: DESKTOP_PER_VIEW,
		slidesToScroll: DESKTOP_PER_VIEW,
		arrows: false,
		infinite: count > DESKTOP_PER_VIEW,
		responsive: [
			{
				breakpoint: 768,
				settings: {
					slidesToShow: MOBILE_PER_VIEW,
					slidesToScroll: MOBILE_PER_VIEW,
					infinite: count > MOBILE_PER_VIEW,
				},
			},
		],
	}
	// slick always runs in its own left-to-right model; StyledGrid mirrors the
	// viewport in Arabic, so "next" advances the same way in both directions.
	const handleNextSlide = () => {
		swiperRef.current?.slickNext()
	}
	const handlePrevSlide = () => {
		swiperRef.current?.slickPrev()
	}
	return (
		<StyledGrid gridTemplateColumns={withArrows ? { sm: '5% 80% 5%', lg: '5% 90% 5%' } : '100%'} alignItems="center">
			{withArrows && (
				<NavIcon onClick={handlePrevSlide} color="purple" show top={'unset'}>
					<IoIosArrowDropleftCircle />
				</NavIcon>
			)}
			<Slider ref={swiperRef} {...settings}>
				{children}
			</Slider>
			{withArrows && (
				<NavIcon onClick={handleNextSlide} color="purple" show top={'unset'}>
					<IoIosArrowDroprightCircle />
				</NavIcon>
			)}
		</StyledGrid>
	)
}

AutoPlaySwiper.Item = SwiperItem

export default AutoPlaySwiper

const StyledGrid = styled(GridLayout).attrs(prop => ({ dir: document.documentElement.dir }))`
	&&[dir='rtl'] {
		${NavIcon} svg {
			transform: scale(-1, 1);
		}

		/* slick only ever lays its track out from the left edge and offsets it with a
		   negative translate, so it cannot start on the first slide in an rtl page —
		   it opened part-way down the list. Mirror the viewport instead and flip each
		   slide back upright: item 1 lands on the right and the rest follow leftwards,
		   with slick still doing its maths left-to-right. The float override undoes
		   slick's own [dir='rtl'] rule, which would otherwise reverse them twice. */
		.slick-list {
			transform: scaleX(-1);
		}
		.slick-slide {
			float: left;
			transform: scaleX(-1);
		}
	}
`
